import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import {
    ComponentPortal,
    ComponentType,
    PortalInjector
} from '@angular/cdk/portal';
import {
    Injectable,
    InjectionToken,
    Injector
} from '@angular/core';
import { Subject } from 'rxjs';
import { DismissEvent } from './events/dismiss.event';
import { ModalConfig, ModalConfigDef } from './modal.config';

export const MODAL_DATA = new InjectionToken<{}>('MODAL_DATA');

@Injectable({
    providedIn: 'root',
})
export class ModalService<T> {
    private _component: ComponentType<T>;
    private _config: ModalConfig;
    private _overlayRef: OverlayRef;
    private _onClosed: Subject<any> = new Subject<any>();

    constructor(private _overlay: Overlay, private _injector: Injector) {}

    component(component: ComponentType<T>) {
        this._component = component;
        return this;
    }

    config(config: ModalConfigDef) {
        this._config = new ModalConfig(config);
        return this;
    }

    open(data?: any): OpenedModal {
        if (!this._component)
            throw 'O componente a ser aberto não foi informado, utilize a função component()';

        this._overlayRef = this._createOverlayRef();
        this._overlayRef.attach(this._createComponentInstance(data));

        window.addEventListener('popstate', (event) => {
            this._overlayRef.detach();
        });

        return {
            dismiss(data?: any) {
                return this.dismiss.call(this, data);
            },
        };
    }

    dismiss<T>(event?: DismissEvent<T>) {
        if (!this._overlayRef.hasAttached()) {
            return;
        }

        this._overlayRef.detach();
        this._onClosed.next(event);

        return this._onClosed;
    }

    onDismiss() {
        return this._onClosed;
    }

    private _injectParams(data: any) {
        const injectorTokens = new WeakMap<any, any>([
            [MODAL_DATA, data || {}],
        ]);
        return new PortalInjector(this._injector, injectorTokens);
    }

    private _createOverlayRef() {
        return this._overlay.create({
            hasBackdrop: true,
            positionStrategy: this._createStrategyPosition(),
        });
    }

    private _createStrategyPosition() {
        return this._overlay
            .position()
            .global()
            .centerHorizontally()
            .centerVertically();
    }

    private _createComponentInstance(data) {
        return new ComponentPortal(
            this._component,
            null,
            this._injectParams(data)
        );
    }
}

interface OpenedModal {
    dismiss();
}
